<template>
  <Dialog
    :open="open"
    @update:open="handleUpdateOpen"
    variant="create"
    background="white"
    :size="maxSmall ? 'full' : undefined"
    :dismissible="!form.isDirty"
    trackingId="tasks.new"
  >
    <slot />
    <template #header>
      <Component
        :is="maxSmall ? DrawerHeader : DialogHeader"
        :title="maxSmall ? $t('components.task_create.title') : undefined"
        class="max-md:border-b max-md:rounded-none"
      >
        <template v-if="form.isDirty" #close>
          <UnsavedChangesModal
            :title="$t('components.task_create.modals.unsaved_changes.title')"
            :description="
              $t('components.task_create.modals.unsaved_changes.description')
            "
            @confirm="handleUpdateOpen(false)"
          >
            <CloseButton
              size="lg"
              :aria-label="$t('components.buttons.close')"
            />
          </UnsavedChangesModal>
        </template>
      </Component>
    </template>
    <template #content>
      <div class="flex flex-col justify-between grow md:max-w-[550px]">
        <div class="flex flex-col grow">
          <div class="flex flex-col grow py-4 px-5 md:px-3 md:pt-0">
            <div
              class="flex flex-col gap-3 ltr:md:pr-14 ltr:md:pl-3 rtl:md:pr-3 rtl:md:pl-14"
            >
              <BuildingSelectModal
                v-model="building"
                variant="create"
                :buildingId="building?.id"
                :title="$t('components.task_create.modals.building.title')"
                :description="
                  $t('components.task_create.modals.building.description')
                "
                :teleport="false"
              />
              <div class="flex flex-col gap-2 md:gap-1">
                <EditableTextArea
                  v-model="form.description"
                  :maxLength="200"
                  :placeholder="$t('components.task_create.title_placeholder')"
                  startWithEditMode
                  class="text-primary placeholder:text-placeholder_subtle data-[placeholder-shown]:data-[empty]:text-placeholder_subtle text-style-2xl font-medium"
                />
                <EditableTextArea
                  v-model="form.remarks"
                  :placeholder="
                    $t('components.task_create.additional_info_placeholder')
                  "
                  class="text-primary placeholder:text-placeholder_subtle data-[placeholder-shown]:data-[empty]:text-placeholder_subtle text-style-base font-medium"
                />
              </div>
            </div>
          </div>
          <div class="py-1.5 px-6">
            <MediaPreview
              :key="form.media.length"
              :media="form.media"
              @deleteMedia="deleteMedia"
            />
          </div>
        </div>
        <TaskCreateProperties
          :key="building?.id"
          :building="building"
          v-model:space="form.space"
          v-model:priority="form.priority"
          v-model:assignees="form.assignees"
          v-model:date="form.date"
          v-model:category="form.category"
          v-model:tags="form.tags"
        />
      </div>
    </template>
    <template #footer>
      <TaskCreateFooter
        @uploadMedia="handleMediaUpload"
        @handleSubmit="handleSubmit"
        :canSubmit="canSubmit"
      />
    </template>
  </Dialog>
</template>

<script setup>
import route from "ziggy-js";
import { ref, inject, computed, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useForm, usePage, router } from "@inertiajs/vue3";
import { CloseButton } from "@/SharedComponents/Buttons";
import { Dialog, DialogHeader } from "@/SharedComponents/Dialog";
import { DrawerHeader } from "@/SharedComponents/Drawer";
import { EditableTextArea } from "@/SharedComponents/Inputs";
import {
  BuildingSelectModal,
  UnsavedChangesModal,
} from "@/SharedComponents/Modals";
import { MediaPreview } from "@/SharedComponents/Parts";
import {
  TaskCreateProperties,
  TaskCreateFooter,
} from "@/SharedComponents/TaskCreate/Parts";
import { toast } from "@/SharedComponents/Toaster";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { toUTC } from "@/Utils/date";
import { includeHours } from "@/Utils/time";
import { useSites } from "@/Composables/useSites";

const { maxSmall } = useBreakpoints();
const { t } = useI18n();
const { isLoading, data: buildings } = useSites();

const emitter = inject("emitter");

const open = ref(false);
const busy = ref(false);

const building = ref(null);
const form = useForm({
  space: null,
  description: "",
  remarks: "",
  assignees: [],
  priority: "normal",
  date: null,
  category: null,
  tags: [],
  media: [],
});

function resetAllFields() {
  form.reset();
  building.value = null;
}

watch(building, () => {
  resetBuildingSensitiveFields();
});

function resetBuildingSensitiveFields() {
  handleUpdateSpace();
  if (!form.isDirty) {
    return;
  }
  let children = building.value?.children;
  form.space =
    form.space && children
      ? children.find((child) => child.id === form.space.id)
      : null;
  form.assignees = form.assignees.filter(
    (assignee) =>
      !building.value || assignee.tenants.includes(building.value.id)
  );
  form.category = null;
  form.tags = form.tags.filter(
    (tag) => !building.value || tag.tenants.includes(building.value.id)
  );
}

const canSubmit = computed(() => {
  return !!(building.value && form.description.length);
});

function handleMediaUpload(files) {
  form.media = form.media.concat(files);
}

function deleteMedia(index) {
  form.media = form.media.filter((_, i) => i !== index);
}

function handleSubmit() {
  toast.promise(
    () =>
      new Promise((resolve, reject) =>
        form
          .transform((data) => {
            return {
              tenant_id: building.value.id,
              description: data.description,
              remarks: data.remarks,
              space_id: data.space?.id,
              assignee_id: data.assignees.map((assignee) => assignee.id),
              priority: data.priority,
              date_scheduled: data.date
                ? toUTC(includeHours(data.date).toISOString())
                : null,
              category_id: data.category?.id,
              tags: data.tags.map((tag) => tag.id),
              photos: data.media.map((item) => item.file),
            };
          })
          .post(route("tasks.add"), {
            onStart: () => (busy.value = true),
            onSuccess: ({ props }) => {
              emitter.emit("task-added", props.new_task);
              open.value = false;
              router.visit(route("tasks.index"), {
                preserveState: true,
              });
              resolve();
            },
            onError: (err) => {
              reject(Object.values(err)[0]);
            },
            onFinish: () => (busy.value = false),
          })
      ),
    {
      loading: t("tasks.loading"),
      success: t("toasts.tasks.create.success"),
      error: (message) => {
        return message ? message : t("toasts.tasks.create.error");
      },
    }
  );
}

watch(
  open,
  () => {
    resetAllFields();
  },
  { immediate: true }
);

function handleUpdateOpen(value) {
  open.value = value;
  const url = new URL(window.location.href);

  if (!value && usePage().url.includes("/admin/tasks/new")) {
    if (url.searchParams.get("redirect")) {
      window.history.back();
    } else {
      router.replace({
        url: route("tasks.index"),
        preserveScroll: true,
        preserveState: true,
      });
    }
  } else {
    router.replace({
      url: route("tasks.add"),
      preserveScroll: true,
      preserveState: true,
    });
  }
}

const handleUpdateSpace = () => {
  if (!isLoading.value) {
    const url = new URL(window.location.href);

    if (url.searchParams.has("spaceId") && url.searchParams.has("buildingId")) {
      const spaceId = url.searchParams.get("spaceId");

      const buildingId = url.searchParams.get("buildingId");

      building.value = buildings.value.find(
        (building) => building.id === buildingId
      );

      let children = building.value?.children;
      form.space = children
        ? children.find((child) => child.id === spaceId)
        : null;
    }
  }
};

onMounted(() => {
  if (usePage().url.includes("/admin/tasks/new")) {
    open.value = true;

    handleUpdateSpace();
  }
});

watch(usePage(), () => {
  if (usePage().url.includes("/admin/tasks/new")) {
    open.value = true;

    handleUpdateSpace();
  }
});

watch(isLoading, () => {
  handleUpdateSpace();
});
</script>
